import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Center,
  Icon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Spinner,
  Text,
  useBreakpointValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { MdLogout, MdArrowDropDown, MdArrowDropUp } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useInstanciaStore } from '@/stores/instancias';
import { useProfessionalStore, useUnidadeStore, useUserStore } from '@/stores';
import { api } from '@/lib';
import { usechangePersonaMutation } from '@/features/auth';
import { GenericModal } from '@/features/registers';
import { ChangeEnvironmentModal } from './components/ChangeEnvironmentModal';

type PersonaTipo =
  | 'paciente'
  | 'medico'
  | 'secretaria'
  | 'gestorUnidade'
  | 'gestorInstancia'
  | 'Master'
  | 'admin';

type PersonaItem = {
  tipo: PersonaTipo;
  label: string;
};

type ChangePersonaPayload = {
  usuarioId: string;
  novaPersona: number;
  instanciaId?: string;
  unidadeId?: string;
  title?: string;
};

const UserTypeNumber = {
  admin: 0,
  Master: 5,
  secretaria: 2,
  medico: 3,
  paciente: 4,
  gestorUnidade: 6,
  gestorInstancia: 7,
};

export function UserInfo() {
  const isMobile = useBreakpointValue({ base: true, md: false });

  const [isOpen, setIsOpen] = useState(false);
  const [openModal, setOpenModal] = useState<ChangePersonaPayload | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [personas, setPersonas] = useState<PersonaItem[]>([]);

  const { mutate } = usechangePersonaMutation();
  const user = useUserStore((state: { user: any }) => state.user);
  const setUser = useUserStore((state: { setUser: any }) => state.setUser);

  const setInstancia = useInstanciaStore(
    (state: { setInstancia: any }) => state.setInstancia,
  );
  const setUnidade = useUnidadeStore(
    (state: { setUnidade: any }) => state.setUnidade,
  );
  const setProfessional = useProfessionalStore(
    (state: { setProfessional: any }) => state.setProfessional,
  );

  const instanceStore = useInstanciaStore(state => state.instancia);
  const unidadeStore = useUnidadeStore(state => state.unidade);

  const navigate = useNavigate();

  const toast = useToast({
    variant: 'solid',
    position: 'top-right',
    status: 'success',
    isClosable: true,
  });

  function handleLogout() {
    localStorage.clear();
    setUser(null);
    setInstancia(null);
    setUnidade(null);
    setProfessional(null);

    toast({ title: 'Até mais!' });
    navigate('/auth');
  }

  const GetPersonas = async () => {
    setIsLoading(true);

    const params = {
      usuarioId: user?.id,
      instanciaId: instanceStore?.id,
      unidadeId: unidadeStore?.id,
    };

    const { data: response } = await api.get(`/usuario/Listar-Personas`, {
      params,
    });

    // Se sua API retornar direto o array:
    setPersonas((response?.data ?? response) as PersonaItem[]);

    setIsLoading(false);
  };

  useEffect(() => {
    if (isOpen && instanceStore?.id && unidadeStore?.id) {
      GetPersonas();
    }
  }, [isOpen, instanceStore, unidadeStore]);

  const changePersonaLogin = (values: ChangePersonaPayload) => {
    setIsLoading(true);

    mutate(values, {
      onSuccess: () => {
        setIsLoading(false);
        setOpenModal(null);
        setIsOpen(false);
        GetPersonas();
      },
      onError: () => {
        setIsLoading(false);
      },
    });
  };

  return (
    <Box>
      <Popover placement="bottom-end">
        {/* @ts-ignore */}
        <PopoverTrigger>
          <Button pr="12" h="60px" bg="transparent" justifyItems="center">
            <VStack spacing="1" mr="4" align="flex-start">
              <Text color="brand-dark.400" fontSize="md" fontWeight="medium">
                {user?.name}
              </Text>
              <Text color="brand-dark.300" fontSize="xs" fontWeight="normal">
                {user?.descricao}
              </Text>
            </VStack>
          </Button>
        </PopoverTrigger>

        <Portal>
          <Box zIndex="popover" w="full" h="full" position="fixed">
            <PopoverContent width="inherit" bg="white" overflow="hidden">
              <PopoverBody p="0" w="min(50vw, 180px)">
                {user?.instanciasId.length > 0 && (
                  <Button
                    onClick={() => setIsOpen(v => !v)}
                    leftIcon={
                      <Icon as={isOpen ? MdArrowDropUp : MdArrowDropDown} />
                    }
                    w="100%"
                    backgroundColor="white"
                    color="brand-dark.400"
                    justifyContent="flex-start"
                  >
                    Alterar persona
                  </Button>
                )}

                {isOpen &&
                  (isLoading ? (
                    <Center w="100%" backgroundColor="brand-dark.600" h="50px">
                      <Spinner color="green.500" size="sm" />
                    </Center>
                  ) : personas && personas.length > 0 ? (
                    personas.map(p => (
                      <Button
                        key={p.tipo}
                        onClick={() => {
                          setOpenModal({
                            usuarioId: user?.id,
                            novaPersona: UserTypeNumber[p.tipo],
                            instanciaId: instanceStore?.id,
                            unidadeId: unidadeStore?.id,
                          });
                        }}
                        w="100%"
                        backgroundColor="brand-dark.600"
                        justifyContent="center"
                        py="2"
                        color="#959595"
                        display="flex"
                        flexDirection="column"
                        fontSize="md"
                        paddingX={10}
                      >
                        {p.label}
                      </Button>
                    ))
                  ) : (
                    <Text py="4" textAlign="center">
                      Nenhuma outra persona
                    </Text>
                  ))}

                <Button
                  onClick={handleLogout}
                  leftIcon={<Icon as={MdLogout} />}
                  w="100%"
                  backgroundColor="white"
                  color="brand-dark.400"
                  justifyContent="flex-start"
                >
                  Sair
                </Button>
              </PopoverBody>
            </PopoverContent>
          </Box>
        </Portal>
      </Popover>

      <GenericModal
        open={!!openModal?.novaPersona}
        title="Alterar persona"
        onClose={() => setOpenModal(null)}
      >
        <ChangeEnvironmentModal
          changeUser
          onChange={() => {
            if (openModal) changePersonaLogin(openModal);
          }}
          title={openModal?.title ?? 'Alterar persona'}
          onCancel={() => setOpenModal(null)}
          isLoading={isLoading}
        />
      </GenericModal>
    </Box>
  );
}
